import React, { useEffect, useState } from 'react';
import {
  FaCalendarAlt,
  FaChevronDown,
  FaCreditCard,
  FaEdit,
  FaEnvelope,
  FaGlobe,
  FaIdCard,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaTrash,
} from 'react-icons/fa';
import { getImageCacheKey, getImageUrl } from '../../../lib/helpers';

const ShopTable = ({ shops, loading, onEdit, onDelete, onToggleSelect, selectedIds = [], onManageBilling, onCancelSubscription, onStartBilling, onStartSetup }) => {
  const [selectedPlans, setSelectedPlans] = useState({});
  /** Móvil/tablet: solo una tienda con el panel de detalle abierto a la vez */
  const [openDetailShopId, setOpenDetailShopId] = useState(null);

  useEffect(() => {
    if (!shops) return;
    setSelectedPlans((prev) => {
      const next = { ...prev };
      shops.forEach((shop) => {
        if (!next[shop.id]) {
          next[shop.id] = shop.plan_tier || 'basico';
        }
      });
      return next;
    });
  }, [shops]);

  /** Si la tienda abierta deja de existir en la lista, cerrar panel */
  useEffect(() => {
    if (openDetailShopId == null || !shops?.length) return;
    const stillThere = shops.some((s) => String(s.id) === String(openDetailShopId));
    if (!stillThere) setOpenDetailShopId(null);
  }, [shops, openDetailShopId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mb-4"></div>
          <p className="text-gray-500">Cargando tiendas...</p>
        </div>
      </div>
    );
  }

  if (!shops || shops.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow border border-gray-200 p-12 text-center">
        <div className="text-gray-400 mb-2">
          <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        </div>
        <p className="text-gray-500 text-lg font-medium">No hay tiendas disponibles</p>
        <p className="text-gray-400 text-sm mt-1">Crea tu primera tienda para comenzar</p>
      </div>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return dateString;
    }
  };

  const renderShopOwners = (owners, maxVisible = 2) => {
    if (!owners || (Array.isArray(owners) && owners.length === 0)) {
      return <span className="text-xs text-gray-400 italic">Sin propietarios</span>;
    }
    
    if (Array.isArray(owners)) {
      const validOwners = owners.filter(owner => owner && (owner.id || (typeof owner === 'string' && owner)));
      
      if (validOwners.length === 0) {
        return <span className="text-xs text-gray-400 italic">Sin propietarios</span>;
      }
      
      const shown = maxVisible >= validOwners.length ? validOwners : validOwners.slice(0, maxVisible);
      const rest = validOwners.length - shown.length;

      return (
        <div className="flex flex-wrap gap-1">
          {shown.map((owner, index) => {
            const ownerId = owner.id || owner;
            const ownerName = owner.name || null;
            const ownerEmail = owner.email || null;
            const displayText = ownerName || ownerEmail || `Propietario ${index + 1}`;
            
            return (
              <span 
                key={`${ownerId}-${index}`} 
                className="inline-flex items-center gap-1 bg-purple-100 text-purple-700 rounded-full px-2 py-0.5 text-xs font-medium"
                title={ownerEmail && ownerName && ownerName !== ownerEmail ? `${ownerName} (${ownerEmail})` : ownerEmail || ownerName || undefined}
              >
                {displayText}
              </span>
            );
          })}
          {rest > 0 && (
            <span className="inline-flex items-center bg-gray-100 text-gray-600 rounded-full px-2 py-0.5 text-xs font-medium">
              +{rest}
            </span>
          )}
        </div>
      );
    }
    
    return <span className="text-xs text-gray-400">Sin propietarios</span>;
  };

  const formatAddress = (shop) => {
    return [shop.address, shop.postal_code, shop.city].filter(Boolean).join(', ') || '-';
  };

  const formatContact = (shop) => {
    return [shop.phone, shop.email].filter(Boolean);
  };

  const planLabels = {
    test: 'Test',
    basico: 'Básico',
    estandar: 'Estándar',
    premium: 'Premium',
  };

  const statusLabels = {
    active: 'Activa',
    trialing: 'Prueba',
    past_due: 'Pago pendiente',
    canceled: 'Cancelada',
    unpaid: 'Impagada',
    incomplete: 'Incompleta',
    incomplete_expired: 'Expirada',
  };
  const activationLabels = {
    active: 'Activa',
    pending: 'Pendiente',
  };
  const selectablePlans = [
    { value: 'test', label: 'Test' },
    { value: 'basico', label: 'Básico' },
    { value: 'estandar', label: 'Estándar' },
    { value: 'premium', label: 'Premium' },
  ];

  const venueTypeLabels = {
    cannabis_club: 'Cannabis Club',
    cbd_shop: 'CBD Shop',
    social_club: 'Social Club',
    bistro: 'Bistro',
  };

  return (
    <div className="bg-white p-3 sm:p-4 rounded-lg shadow border border-gray-200 flex-auto">
      {/* Listado responsive (móvil + tablet vertical) */}
      <div className="block lg:hidden md:max-lg:landscape:hidden space-y-3">
        {shops.map((shop) => {
          const imageUrl = getImageUrl(shop.image_path);

          return (
            <div
              key={shop.id}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:shadow-md hover:border-purple-300 transition-all duration-200"
            >
              <div className="mb-3 flex justify-end">
                <input
                  type="checkbox"
                  checked={selectedIds.some((id) => String(id) === String(shop.id))}
                  onChange={() => onToggleSelect(shop.id)}
                  className="h-4 w-4 accent-purple-600"
                />
              </div>
              <div className="mb-2 flex items-start gap-3">
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={shop.name}
                    className="h-32 w-32 flex-shrink-0 rounded-xl border-2 border-gray-300 object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      if (e.target.nextSibling) {
                        e.target.nextSibling.style.display = 'flex';
                      }
                    }}
                  />
                ) : null}
                <div
                  className={`flex h-32 w-32 flex-shrink-0 items-center justify-center rounded-xl bg-purple-200 text-2xl font-bold text-purple-700 ${imageUrl ? 'hidden' : 'flex'}`}
                >
                  {shop.name?.charAt(0)?.toUpperCase() || 'T'}
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setOpenDetailShopId((current) =>
                      current === shop.id || String(current) === String(shop.id) ? null : shop.id
                    )
                  }
                  className="flex min-w-0 flex-1 items-start gap-2 rounded-lg py-0.5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2"
                  aria-expanded={openDetailShopId === shop.id || String(openDetailShopId) === String(shop.id)}
                  aria-controls={`shop-table-details-${shop.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-semibold text-gray-900">{shop.name || '-'}</div>
                    <div className="truncate text-xs text-gray-500">ID: {shop.id?.toString().slice(0, 8) || '-'}</div>
                  </div>
                  <FaChevronDown
                    className={`mt-0.5 h-5 w-5 shrink-0 text-purple-600 transition-transform duration-300 ${
                      openDetailShopId === shop.id || String(openDetailShopId) === String(shop.id) ? 'rotate-180' : ''
                    }`}
                    aria-hidden
                  />
                </button>
              </div>

              <div
                id={`shop-table-details-${shop.id}`}
                className={
                  openDetailShopId === shop.id || String(openDetailShopId) === String(shop.id)
                    ? 'mb-4 block'
                    : 'hidden'
                }
              >
                <div className="space-y-3 rounded-lg bg-gray-50 p-3">
                  {shop.venue_type ? (
                    <span className="inline-flex rounded-full bg-purple-100 px-2.5 py-1 text-xs font-semibold text-purple-700">
                      {venueTypeLabels[shop.venue_type] || shop.venue_type}
                    </span>
                  ) : null}

                  <div>
                    <p className="mb-1 text-xs font-medium text-gray-500">Dirección</p>
                    <div className="flex items-start gap-2 text-sm text-gray-700">
                      <FaMapMarkerAlt className="mt-0.5 shrink-0 text-xs text-gray-400" />
                      <span className="min-w-0 break-words">{formatAddress(shop)}</span>
                    </div>
                  </div>

                  <div>
                    <p className="mb-1 text-xs font-medium text-gray-500">Contacto</p>
                    {formatContact(shop).length > 0 || shop.website || shop.tax_id ? (
                      <div className="space-y-1.5 text-sm text-gray-700">
                        {shop.phone ? (
                          <div className="flex items-center gap-2">
                            <FaPhoneAlt className="shrink-0 text-xs text-gray-400" />
                            <span>{shop.phone}</span>
                          </div>
                        ) : null}
                        {shop.email ? (
                          <div className="flex items-start gap-2">
                            <FaEnvelope className="mt-0.5 shrink-0 text-xs text-gray-400" />
                            <span className="break-all">{shop.email}</span>
                          </div>
                        ) : null}
                        {shop.website ? (
                          <div className="flex items-start gap-2">
                            <FaGlobe className="mt-0.5 shrink-0 text-xs text-gray-400" />
                            <span className="break-all text-xs text-gray-600">{shop.website}</span>
                          </div>
                        ) : null}
                        {shop.tax_id ? (
                          <div className="flex items-center gap-2">
                            <FaIdCard className="shrink-0 text-xs text-gray-400" />
                            <span className="text-xs text-gray-600">CIF/NIF: {shop.tax_id}</span>
                          </div>
                        ) : null}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">Sin datos de contacto</span>
                    )}
                  </div>

                  <div className="rounded-lg border border-gray-200 bg-white p-3">
                    <div className="mb-2 flex items-center gap-1.5">
                      <FaCreditCard className="text-xs text-gray-400" />
                      <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Facturación</span>
                    </div>
                    <div className="mb-2 flex flex-wrap gap-2">
                      <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700">
                        Plan {planLabels[shop.plan_tier] || 'Básico'}
                      </span>
                      <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                        Alta: {activationLabels[shop.activation_status] || 'Pendiente'}
                      </span>
                      <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                        Suscripción:{' '}
                        {statusLabels[shop.subscription_status] || (shop.stripe_customer_id ? 'Pendiente' : 'Sin activar')}
                      </span>
                    </div>
                    <label className="mb-2 block text-xs font-medium text-gray-600">Plan para activar</label>
                    <select
                      value={selectedPlans[shop.id] || shop.plan_tier || 'basico'}
                      onChange={(e) =>
                        setSelectedPlans((prev) => ({
                          ...prev,
                          [shop.id]: e.target.value,
                        }))
                      }
                      className="mb-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700"
                    >
                      {selectablePlans.map((plan) => (
                        <option key={plan.value} value={plan.value}>
                          {plan.label}
                        </option>
                      ))}
                    </select>
                    {shop.activation_status === 'active' && shop.stripe_subscription_id ? (
                      <div className="grid grid-cols-1 gap-2">
                        <button
                          type="button"
                          onClick={() => onManageBilling(shop.id)}
                          className="w-full rounded-lg border border-purple-300 bg-white px-3 py-2.5 text-sm font-medium text-purple-700 hover:bg-purple-50"
                        >
                          Gestionar facturación
                        </button>
                        <button
                          type="button"
                          onClick={() => onCancelSubscription(shop.id)}
                          className="w-full rounded-lg border border-red-300 bg-white px-3 py-2.5 text-sm font-medium text-red-700 hover:bg-red-50"
                        >
                          Cancelar suscripción
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() =>
                          shop.activation_status !== 'active'
                            ? onStartSetup(shop.id, selectedPlans[shop.id] || shop.plan_tier || 'basico')
                            : onStartBilling(shop.id, selectedPlans[shop.id] || shop.plan_tier || 'basico')
                        }
                        className="w-full rounded-lg bg-purple-600 px-3 py-2.5 text-sm font-medium text-white hover:bg-purple-700"
                      >
                        {shop.activation_status !== 'active' ? 'Pagar alta' : 'Activar suscripción'}
                      </button>
                    )}
                  </div>

                  <div>
                    <p className="mb-1 text-xs font-medium text-gray-500">Propietarios</p>
                    {renderShopOwners(shop.owners, 50)}
                  </div>

                  <div className="flex items-center gap-2 border-t border-gray-200 pt-2 text-sm text-gray-700">
                    <FaCalendarAlt className="shrink-0 text-xs text-gray-400" />
                    <span>
                      Creada: <span className="font-medium">{formatDate(shop.date_created)}</span>
                    </span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => onEdit(shop.id)}
                  className="flex items-center justify-center gap-2 px-3 py-2.5 bg-lime-600 hover:bg-lime-700 text-white rounded-lg text-sm font-semibold shadow-sm hover:shadow transition-all active:scale-95"
                  title="Editar tienda"
                >
                  <FaEdit />
                  <span>Editar</span>
                </button>
                <button
                  onClick={() => onDelete(shop.id)}
                  className="flex items-center justify-center gap-2 px-3 py-2.5 bg-red-700 hover:bg-red-800 text-white rounded-lg text-sm font-semibold shadow-sm hover:shadow transition-all active:scale-95"
                  title="Eliminar tienda"
                >
                  <FaTrash />
                  <span>Eliminar</span>
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Listado en tabla (desktop + tablet apaisada) */}
      <div className="hidden md:max-lg:landscape:block lg:block overflow-x-auto">
        <table className="w-full text-left text-gray-700">
          <thead className="text-xs text-gray-700 uppercase bg-gray-50">
            <tr>
              <th scope="col" className="py-3 px-4 w-12">
                <span className="sr-only">Seleccionar</span>
              </th>
              <th scope="col" className="py-3 px-4">
                Tienda
              </th>
              <th scope="col" className="py-3 px-4">
                Dirección
              </th>
              <th scope="col" className="py-3 px-4">
                Contacto
              </th>
              <th scope="col" className="py-3 px-4">
                Facturación
              </th>
              <th scope="col" className="py-3 px-4">
                Propietarios
              </th>
              <th scope="col" className="py-3 px-4">
                Fecha Creación
              </th>
              <th scope="col" className="py-3 px-4 text-right">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody>
            {shops.map((shop) => {
              const imageUrl = getImageUrl(shop.image_path, {
                cacheKey: getImageCacheKey('venue', shop.id),
              });

              return (
                <tr key={shop.id} className="border-b odd:bg-white even:bg-gray-50 hover:bg-gray-100">
                  <td className="py-3 px-4">
                    <input
                      type="checkbox"
                      checked={selectedIds.some((id) => String(id) === String(shop.id))}
                      onChange={() => onToggleSelect(shop.id)}
                      className="h-4 w-4 accent-purple-600"
                    />
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={shop.name}
                          className="h-12 w-12 rounded object-cover border border-gray-300"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            if (e.target.nextSibling) {
                              e.target.nextSibling.style.display = 'flex';
                            }
                          }}
                        />
                      ) : null}
                      <div
                        className={`h-12 w-12 rounded-full bg-purple-200 flex items-center justify-center text-purple-700 font-bold text-lg ${imageUrl ? 'hidden' : 'flex'}`}
                      >
                        {shop.name?.charAt(0)?.toUpperCase() || 'T'}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{shop.name || '-'}</div>
                        <div className="text-xs text-gray-500">ID: {shop.id?.toString().slice(0, 8) || '-'}</div>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <FaMapMarkerAlt className="text-gray-400 text-xs" />
                      <span className="text-sm text-gray-700">{formatAddress(shop)}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    {formatContact(shop).length > 0 ? (
                      <div className="text-sm text-gray-700 space-y-1">
                        {shop.phone && <div>{shop.phone}</div>}
                        {shop.email && <div className="break-all">{shop.email}</div>}
                        {shop.website && <div className="break-all text-xs text-gray-500">{shop.website}</div>}
                        {shop.tax_id && <div className="text-xs text-gray-500">CIF/NIF: {shop.tax_id}</div>}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <div className="space-y-2">
                      <div className="flex flex-wrap gap-2">
                        <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700">
                          Plan {planLabels[shop.plan_tier] || 'Básico'}
                        </span>
                        <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                          Alta: {activationLabels[shop.activation_status] || 'Pendiente'}
                        </span>
                        <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                          Suscripción: {statusLabels[shop.subscription_status] || (shop.stripe_customer_id ? 'Pendiente' : 'Sin activar')}
                        </span>
                      </div>
                      <select
                        value={selectedPlans[shop.id] || shop.plan_tier || 'basico'}
                        onChange={(e) =>
                          setSelectedPlans((prev) => ({
                            ...prev,
                            [shop.id]: e.target.value,
                          }))
                        }
                        className="w-full rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-700"
                      >
                        {selectablePlans.map((plan) => (
                          <option key={plan.value} value={plan.value}>
                            {plan.label}
                          </option>
                        ))}
                      </select>
                      {shop.activation_status === 'active' && shop.stripe_subscription_id ? (
                        <div className="flex flex-col gap-1.5">
                          <button
                            type="button"
                            onClick={() => onManageBilling(shop.id)}
                            className="rounded-lg border border-purple-300 bg-white px-3 py-1.5 text-xs font-medium text-purple-700 hover:bg-purple-50"
                          >
                            Gestionar facturación
                          </button>
                          <button
                            type="button"
                            onClick={() => onCancelSubscription(shop.id)}
                            className="rounded-lg border border-red-300 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50"
                          >
                            Cancelar suscripción
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() =>
                            shop.activation_status !== 'active'
                              ? onStartSetup(shop.id, selectedPlans[shop.id] || shop.plan_tier || 'basico')
                              : onStartBilling(shop.id, selectedPlans[shop.id] || shop.plan_tier || 'basico')
                          }
                          className="rounded-lg bg-purple-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-purple-700"
                        >
                          {shop.activation_status !== 'active' ? "Pagar alta" : "Activar suscripción"}
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    {renderShopOwners(shop.owners)}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <FaCalendarAlt className="text-gray-400 text-xs" />
                      <span className="text-sm text-gray-700">{formatDate(shop.date_created)}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4 align-top">
                    <div className="hidden justify-end gap-1 lg:flex">
                      <button
                        type="button"
                        onClick={() => onEdit(shop.id)}
                        className="rounded bg-lime-600 p-2 text-white hover:bg-lime-800"
                        title="Editar tienda"
                      >
                        <FaEdit />
                      </button>
                      <button
                        type="button"
                        onClick={() => onDelete(shop.id)}
                        className="rounded bg-red-700 p-2 text-white hover:bg-red-800"
                        title="Eliminar tienda"
                      >
                        <FaTrash />
                      </button>
                    </div>
                    <div className="hidden min-w-[6.75rem] flex-col gap-1.5 md:max-lg:landscape:flex">
                      <button
                        type="button"
                        onClick={() => onEdit(shop.id)}
                        className="flex items-center justify-center gap-1.5 rounded-lg bg-lime-600 px-2.5 py-2 text-xs font-semibold text-white shadow-sm hover:bg-lime-700"
                        title="Editar tienda"
                      >
                        <FaEdit className="shrink-0" />
                        <span>Editar</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => onDelete(shop.id)}
                        className="flex items-center justify-center gap-1.5 rounded-lg bg-red-700 px-2.5 py-2 text-xs font-semibold text-white shadow-sm hover:bg-red-800"
                        title="Eliminar tienda"
                      >
                        <FaTrash className="shrink-0" />
                        <span>Eliminar</span>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ShopTable;


